import { Box, Tooltip, Typography } from '@mui/material';
import { fullDateTime, shortDateTime } from '../../shared/dateTime';

/** Ocena dowiezienia z API (`user-offers/delivery-verdict.ts`). */
export interface DeliveryEstimate {
  willMakeIt: boolean | null;
  reason: string | null;
  arrivalAt: string | null;
  /** Przyjazd z ulgami 561/2006 (10 h jazdy, 9 h odpoczynku), gdy tylko one ratują termin. */
  arrivalWithReliefsAt?: string | null;
  route: { km: number; source: 'tms' | 'szacunek' } | null;
  assumptions: string[];
  /**
   * Przyjazd na załadunek wobec KOŃCA okna załadunku (21.10.2026, przełącznik
   * `loadingWindowEnd`); brak = nie sprawdzano.
   */
  loadingWindow?: {
    late: boolean;
    arrivalWithReliefsAt?: string | null;
    reason: string | null;
  };
}

/**
 * Szacowany przyjazd na rozładunek („pełne tacho”, dokument klienta „Czas
 * pracy kierowcy”). Czerwony, gdy wypada po końcu okna rozładunku; trasa
 * i założenia w dymku, bo liczba bez nich wyglądałaby na fakt.
 *
 * Data bez sekund i roku („01.10, 03:08"), a podpis „po oknie” / „tylko
 * z ulgami” w osobnej linii — w jednej linii z pełną datą komórka łamała się
 * na trzy wiersze. Pełny zapis przyjazdu jest pierwszym zdaniem dymka.
 */
export function DeliveryCell({ delivery }: { delivery?: DeliveryEstimate }) {
  if (!delivery) return <>—</>;
  if (delivery.arrivalAt === null) {
    return (
      <Tooltip title={delivery.reason ?? 'brak danych'}>
        <span>—</span>
      </Tooltip>
    );
  }
  const route = delivery.route
    ? `trasa ${Math.round(delivery.route.km)} km${
        delivery.route.source === 'szacunek' ? ' (szacunek)' : ' (z TMS)'
      }`
    : '';
  // Bez ulg po oknie, z ulgami w oknie — to nie to samo co „nie dowiezie”:
  // decyzja, czy kierowca ma jeszcze wydłużoną jazdę, zostaje u planisty.
  const reliefs = delivery.willMakeIt === false && !!delivery.arrivalWithReliefsAt;
  const shown = reliefs ? (delivery.arrivalWithReliefsAt as string) : delivery.arrivalAt;
  const title = [
    `przyjazd ${fullDateTime(shown)}`,
    reliefs ? delivery.reason : null,
    route,
    ...delivery.assumptions,
  ]
    .filter(Boolean)
    .join('; ');
  const color = reliefs
    ? 'warning.main'
    : delivery.willMakeIt === false
      ? 'error.main'
      : undefined;
  return (
    <Tooltip title={title}>
      <Box sx={{ color }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', color: 'inherit' }}>
          {shortDateTime(shown)}
        </Typography>
        {reliefs || delivery.willMakeIt === false ? (
          <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'nowrap', color: 'inherit' }}>
            {reliefs ? 'tylko z ulgami' : 'po oknie'}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
}
